import * as LucideIcons from 'lucide-react';
import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import MediaListBlock from './MediaListBlock';

interface Icon {
  type: 'lucide' | 'url' | 'image';
  value: string;
  size?: 'small' | 'medium' | 'large';
  color?: string;
  crop?: 'circle';
}

interface InlineIconProps {
  icon: Icon;
}

// Simple icon renderer for Icon objects coming from the backend (lucide or url/image)
const InlineIcon: React.FC<InlineIconProps> = ({ icon }) => {
  if (!icon) return null;
  const sizeMap = { small: 16, medium: 18, large: 22 };
  const size = sizeMap[(icon.size || 'medium').toLowerCase()] || 18;

  if (icon.type === 'lucide') {
    const iconName = (icon.value || '')
      .split(/[-_]/)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join('');
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? (
      <IconComponent
        size={size}
        color={icon.color || 'currentColor'}
        strokeWidth={1.5}
        style={{ marginRight: 6 }}
      />
    ) : null;
  }
  if (icon.type === 'url' || icon.type === 'image') {
    return (
      <img
        src={icon.value}
        alt=""
        style={{
          width: size,
          height: size,
          objectFit: 'cover',
          borderRadius: icon.crop === 'circle' ? '50%' : 0,
          marginRight: 6,
        }}
      />
    );
  }
  return null;
};

const getProp = (obj: any, snake: string, camel: string) =>
  obj && (obj[snake] !== undefined ? obj[snake] : obj[camel]);

interface TreeNode {
  id: string;
  label: string;
  icon?: Icon;
  children?: TreeNode[];
  action?: {
    type: 'navigate';
    url: string;
  };
  is_expanded?: boolean;
  isExpanded?: boolean;
  is_selected?: boolean;
  isSelected?: boolean;
}

interface TreeNodeRowProps {
  node: TreeNode;
  showIcons: boolean;
  depth: number;
  onSelect: (node: TreeNode) => void;
  selectedId: string | null;
}

const TreeNodeRow: React.FC<TreeNodeRowProps> = ({
  node,
  showIcons,
  depth,
  onSelect,
  selectedId,
}) => {
  const defaultOpen = !!getProp(node, 'is_expanded', 'isExpanded');
  const [open, setOpen] = useState<boolean>(defaultOpen);
  const children = node?.children || [];
  const isTerminal = children.length === 0;
  const isSelected = selectedId === node.id;

  const paddingLeft = 8 + depth * 14;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isTerminal) {
      onSelect(node);
    } else {
      setOpen(o => !o);
    }
  };

  const handleDoubleClick = () => {
    // Terminal node may have navigate action
    if (
      isTerminal &&
      node.action &&
      node.action.type === 'navigate' &&
      node.action.url
    ) {
      window.location.assign(node.action.url);
    }
  };

  return (
    <div style={{ paddingLeft }}>
      <div
        className={`d-flex align-items-center py-1 ${isSelected ? 'fw-semibold text-primary' : ''}`}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        role="button"
        aria-expanded={open}
      >
        {!isTerminal && (
          <span style={{ width: 14, display: 'inline-block' }}>
            {open ? '▾' : '▸'}
          </span>
        )}
        {isTerminal && <span style={{ width: 14, display: 'inline-block' }} />}
        {showIcons && <InlineIcon icon={node.icon} />}
        <span>{node.label}</span>
      </div>
      {!isTerminal &&
        open &&
        children &&
        children.map(child => (
          <TreeNodeRow
            key={child.id}
            node={child}
            showIcons={showIcons}
            depth={depth + 1}
            onSelect={onSelect}
            selectedId={selectedId}
          />
        ))}
    </div>
  );
};

interface TreeWithDetailBlockData {
  nodes?: TreeNode[];
  selected_node_id?: string;
  selectedNodeId?: string;
  detail_card_api?: string;
  detailCardApi?: string;
  left_panel_width?: string;
  leftPanelWidth?: string;
  show_icons?: boolean;
  showIcons?: boolean;
  detail_media?: any;
  detailMedia?: any;
  empty_detail_text?: string;
  emptyDetailText?: string;
}

interface TreeWithDetailBlockProps {
  block: TreeWithDetailBlockData;
}

const TreeWithDetailBlock: React.FC<TreeWithDetailBlockProps> = ({ block }) => {
  const initialSelected =
    getProp(block, 'selected_node_id', 'selectedNodeId') || null;
  const [selectedId, setSelectedId] = useState<string | null>(initialSelected);
  const [detailCard, setDetailCard] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // find initial default selected node id if provided in any node
  useEffect(() => {
    if (getProp(block, 'selected_node_id', 'selectedNodeId')) return; // already set
    // scan for a node with is_selected
    const stack = [...(block.nodes || [])];
    while (stack.length) {
      const n = stack.pop();
      if (getProp(n, 'is_selected', 'isSelected')) {
        setSelectedId(n.id);
        return;
      }
      if (n?.children) stack.push(...n.children);
    }
  }, [block]);

  const allNodesById = useMemo(() => {
    const map: Record<string, TreeNode> = {};
    const stack = [...(block.nodes || [])];
    while (stack.length) {
      const n = stack.pop();
      if (n) {
        map[n.id] = n;
        if (n.children) stack.push(...n.children);
      }
    }
    return map;
  }, [block.nodes]);

  const loadDetailCard = async (nodeId: string) => {
    const detailApi = getProp(block, 'detail_card_api', 'detailCardApi');
    if (!detailApi) return setDetailCard(null);
    try {
      setLoading(true);
      setError(null);
      const url = detailApi.replace('{id}', encodeURIComponent(nodeId));
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`Detail API failed (${res.status})`);
      const json = await res.json();
      setDetailCard(json);
    } catch (e) {
      console.error(e);
      setError(e.message);
      setDetailCard(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedId) {
      loadDetailCard(selectedId);
    }
  }, [selectedId, getProp(block, 'detail_card_api', 'detailCardApi')]);

  const handleSelect = (node: TreeNode) => {
    setSelectedId(node.id);
    // if terminal node has navigate action and no detail pane api, still allow single-click selection without redirect
  };

  const leftWidth =
    getProp(block, 'left_panel_width', 'leftPanelWidth') || '30%';
  const showIcons = getProp(block, 'show_icons', 'showIcons');
  const detailMedia = getProp(block, 'detail_media', 'detailMedia');
  const emptyDetailText =
    getProp(block, 'empty_detail_text', 'emptyDetailText') ||
    'Select an item from the tree to view details';

  return (
    <div className="tree-with-detail d-flex" style={{ gap: 16 }}>
      <div
        className="tree-panel border rounded"
        style={{
          flex: '0 0 auto',
          width: leftWidth,
          padding: 8,
          overflowY: 'auto',
        }}
      >
        {(block.nodes || []).map(root => (
          <TreeNodeRow
            key={root.id}
            node={root}
            showIcons={showIcons !== false}
            depth={0}
            onSelect={handleSelect}
            selectedId={selectedId}
          />
        ))}
      </div>
      <div
        className="detail-panel flex-grow-1 border rounded"
        style={{ padding: 12, minHeight: 120 }}
      >
        {loading && <div className="text-muted">Loading details…</div>}
        {error && <div className="text-danger small mb-2">{error}</div>}
        {!loading && !error && detailCard && (
          <Suspense fallback={<div className="text-muted">Rendering…</div>}>
            {/**
             * Lazy-load CardRenderer to avoid circular import between
             * BlockRenderer -> TreeWithDetailBlock -> CardRenderer -> BlockRenderer
             */}
            {(() => {
              const LazyCardRenderer = lazy(() => import('../CardRenderer'));
              return <LazyCardRenderer card={detailCard} />;
            })()}
          </Suspense>
        )}
        {!loading && !detailCard && detailMedia && (
          <MediaListBlock block={detailMedia} />
        )}
        {!loading && !detailCard && !detailMedia && (
          <div className="text-muted">{emptyDetailText}</div>
        )}
      </div>
    </div>
  );
};

export default TreeWithDetailBlock;
